import Uppy, { type Body, type Meta, type RestrictionError, type UploadResult, type UppyFile } from '@uppy/core'
import UppyAwsS3 from '@uppy/aws-s3'
import DropTarget from '@uppy/drop-target'
import UppyThumbnailGenerator from '@uppy/thumbnail-generator'
import {
  createDefaultImageReader,
  createDefaultImageWriter,
  openEditor,
  plugin_crop,
  plugin_filter,
  plugin_filter_defaults,
  plugin_finetune,
  plugin_finetune_defaults,
  setPlugins,
} from '@pqina/pintura'
import '@pqina/pintura/pintura.css'
import useEditorWithUppy from '~/vendor/doka/uppy/useEditorWithUppy'

import type { PinturaCropAspectRatio } from '~/constants/pintura'
import {
  PINTURA_CROP_ICON_CROP_BOUNDARY,
  PINTURA_CROP_ICON_SELECT_PRESET,
} from '~/constants/pintura'

import {
  locale_en_gb,
  plugin_crop_locale_en_gb,
  plugin_filter_locale_en_gb,
  plugin_finetune_locale_en_gb,
} from '~/vendor/doka/doka'

import enMessages from '~~/i18n/locales/en/doka.json'
import frMessages from '~~/i18n/locales/fr/doka.json'

import {
  attachPinturaGrandfatheredPhotoEditorHooks,
  enhanceEditorOptionsForGrandfatheredPhotos,
  processedGrandfatheredOutputPreservesSource,
  resolveGrandfatheredPhotoCropMinSize,
} from '~/utils/pintura-crop-rotate'
import { MAX_IMAGE_UPLOAD_SIZE_MB } from '~/constants/file'
import { exceedsMaxImageUploadSize, getFreeCropImageMinSize } from '~/utils/uppy-local-image-dimensions'
import { readImageDimensions } from '~/utils/pintura'
import type { FileProgressStarted } from '@uppy/utils'
import type { Nullable } from '~/types'
import type { FileInterface } from '~/types/files'
import { isPersistedListingPhoto } from '~/utils/files'

type UploadParams = {
  ObjectKey?: Nullable<string>
  UploadUrl?: Nullable<string>
}

const DEFAULT_MAX_FILES_PER_SELECTION = 20

export default function useUppy(options: {
  dropTargetEl?: Nullable<HTMLElement>
  thumbnail?: {
    height?: number
  }
  awsS3?: {
    getUploadParams: (params: { fileName: string, fileType: string, fileExt: string }) => Promise<UploadParams>
  }
  attachEditor?: boolean
  maxFilesPerSelection?: number
  dokaOptions?: {
    imageCropAspectRatio?: PinturaCropAspectRatio
    maxSize?: number
  }
  /**
   * When set, local image files from the file input are decoded and must meet these
   * minimum long/short edge sizes before `addFile` runs (e.g. RV listing photos).
   */
  minimumImageDimensions?: {
    minLongSide: number
    minShortSide: number
  }
}, initialFiles?: FileInterface[], maxNumberOfFiles: number = 1) {
  const { addToast } = useToast()
  const { t, te, mergeLocaleMessage } = useI18n({
    useScope: 'global',
  })

  /* v8 ignore start */
  mergeLocaleMessage('en', enMessages)
  mergeLocaleMessage('fr', frMessages)

  const dokaLocale = computed(() => {
    const locale = {
      ...locale_en_gb,
      ...plugin_crop_locale_en_gb,
      ...plugin_filter_locale_en_gb,
      ...plugin_finetune_locale_en_gb,
    }

    Object.keys(locale).forEach((key) => {
      const path = `doka.${key}`

      if (te(path)) {
        locale[key] = t(path)
      }
    })

    // Pintura 8 requires these SVG templates on `locale`; vendor locale omits them.
    // Defined in code (not i18n) so vue-i18n does not warn about “HTML” / XSS in messages.
    const localeWithIcons = locale as Record<string, unknown>
    localeWithIcons.cropIconSelectPreset = PINTURA_CROP_ICON_SELECT_PRESET
    localeWithIcons.cropIconCropBoundary = PINTURA_CROP_ICON_CROP_BOUNDARY

    locale.labelSupportError = (features: string[]) => `${features.join(', ')} ${t('doka.notSupportedOnThisBrowser')}`

    locale.statusLabelLoadImage = (state: { task?: string, error?: boolean }) => {
      if (!state?.task) {
        return t('doka.waitingForImage')
      }

      if (state.error) {
        return t('doka.errorLoadingImage')
      }

      if (state.task === 'blob-to-bitmap') {
        return t('doka.creatingPreview')
      }

      return t('doka.loadingImage')
    }

    locale.statusLabelProcessImage = (state: { task?: string, error?: boolean }) => {
      if (!state?.task) {
        return undefined
      }

      if (state.task === 'store') {
        if (state.error) {
          return t('doka.errorUploadingImage')
        }

        return t('doka.uploadingImage')
      }

      if (state.error) {
        return t('doka.errorProcessingImage')
      }

      return t('doka.processingImage')
    }

    return locale
  })
  /* v8 ignore stop */

  const allowedFileTypes = ['.jpg', '.jpeg', '.png', '.webp']

  /**
   * When DropTarget (or any path) adds files without the Doka queue, dimension checks are async.
   * `autoProceed` would start S3 upload in the same tick as `file-added` / `files-added`, before
   * decoding finishes, so we defer uploads until after validation for this configuration only.
   */
  const deferAutoProceedUntilAfterLocalMinimumImageValidation = Boolean(
    options?.minimumImageDimensions && !options?.attachEditor,
  )

  const uppy = new Uppy({
    autoProceed: !deferAutoProceedUntilAfterLocalMinimumImageValidation,
    restrictions: {
      allowedFileTypes,
      maxNumberOfFiles: Math.max(1, options.maxFilesPerSelection ?? DEFAULT_MAX_FILES_PER_SELECTION),
    },
  })

  // Check if we are in a component
  const instance = getCurrentInstance()
  if (instance) {
    onBeforeUnmount(() => {
      uppy.destroy()
    })
  }

  if (options?.dropTargetEl) {
    uppy.use(DropTarget, { target: options.dropTargetEl })
  }

  if (options?.thumbnail?.height) {
    uppy.use(UppyThumbnailGenerator, { thumbnailHeight: options.thumbnail.height })
  }

  if (options?.awsS3?.getUploadParams) {
    uppy.use(UppyAwsS3, {
      limit: 1,
      shouldUseMultipart: false,
      generateObjectKey: (file) => file.id,
      async signRequest({ method, key }) {
        if (method !== 'PUT') {
          throw new Error(`Unexpected S3 method: ${method}`)
        }

        const file = uppy.getFile(key)
        const fileExt = getImageExtensionFromMimeType(file.type, file.name) ?? 'jpg'
        const params = await options.awsS3!.getUploadParams({
          fileName: file.name ?? '',
          fileType: file.type ?? '',
          fileExt,
        })

        file.meta.s3ObjectKey = params.ObjectKey

        return { url: params.UploadUrl!, key: params.ObjectKey! }
      },
    })
  }

  const files = ref(initialFiles ?? [])
  const remainingMaxNumberOfFiles = computed(() => maxNumberOfFiles - files.value.length)

  setPlugins(plugin_crop, plugin_filter, plugin_finetune)

  async function resolveOpenCropAspectRatioForUppyFile(
    _file: UppyFile<Meta, Body>,
  ): Promise<PinturaCropAspectRatio | undefined> {
    return options?.dokaOptions?.imageCropAspectRatio
  }

  function resolveListingPhotoCropMinSize() {
    return getFreeCropImageMinSize(options.minimumImageDimensions!.minShortSide)
  }

  const isListingPhotoEditor
    = Boolean(options?.attachEditor)
      && options?.dokaOptions?.imageCropAspectRatio == null

  const editorOptions = computed(() => {
    return {
      imageReader: createDefaultImageReader(),
      imageWriter: createDefaultImageWriter({
        targetSize: {
          width: options?.dokaOptions?.maxSize ?? 0,
          height: options?.dokaOptions?.maxSize ?? 0,
        },
      }),

      imageCropAspectRatio: options?.dokaOptions?.imageCropAspectRatio,
      imageCropLimitToImage: true,

      cropEnableButtonFlipVertical: true,
      cropEnableButtonRotateRight: true,
      cropEnableZoomTowardsWheelPosition: false,
      cropEnableSelectPreset: false,
      ...(isListingPhotoEditor
        ? {
            cropEnableButtonRotateLeft: true,
            cropEnableRotationInput: false,
          }
        : {}),

      ...plugin_filter_defaults,
      ...plugin_finetune_defaults,

      locale: dokaLocale.value,
    }
  })

  function resolveUploadFileExtension(fileNameOrExtension?: string): string {
    if (!fileNameOrExtension) {
      return ''
    }

    const normalized = fileNameOrExtension.toLowerCase()
    if (normalized.startsWith('.')) {
      return normalized.slice(1)
    }

    return normalized.split('.').pop() ?? ''
  }

  function isLocalImageUpload(blob: Blob, mimeType: string, fileNameOrExtension?: string): boolean {
    if (!(blob instanceof Blob)) {
      return false
    }

    if ((mimeType ?? '').startsWith('image/')) {
      return true
    }

    const extension = resolveUploadFileExtension(fileNameOrExtension)
    return allowedFileTypes.includes(`.${extension}`)
  }

  /**
   * Rejects local image files above the configured upload size cap before decode/editor work.
   *
   * @param blob The blob to validate
   * @param mimeType The mime type of the blob
   * @param fileNameOrExtension File name or extension when MIME is missing or generic
   * @returns True when the blob is within the upload size limit or not a local image
   */
  function validateLocalImageBlobWithinMaxUploadSize(
    blob: Blob,
    mimeType: string,
    fileNameOrExtension?: string,
  ): boolean {
    if (!isLocalImageUpload(blob, mimeType, fileNameOrExtension)) {
      return true
    }

    if (exceedsMaxImageUploadSize(blob.size)) {
      addToast({
        message: t('doka.imageTooLarge', { maxSizeMb: MAX_IMAGE_UPLOAD_SIZE_MB }),
        variant: 'danger',
      })
      return false
    }

    return true
  }

  /**
   * Shared by the file input path, optional `beforeOpenEditor` in the Doka queue, and
   * `files-added` when there is no editor (DropTarget adds files straight into Uppy).
   *
   * @param blob The blob to validate
   * @param mimeType The mime type of the blob
   * @returns A promise that resolves when the blob is validated
   */
  async function validateLocalImageBlobMeetsConfiguredMinimum(blob: Blob, mimeType: string): Promise<boolean> {
    const minDims = options?.minimumImageDimensions
    if (!minDims || !(mimeType ?? '').startsWith('image/') || !(blob instanceof Blob)) {
      return true
    }

    try {
      const { width, height } = await getDecodedImageDimensionsFromBlob(blob)
      if (!meetsMinimumImageDimensions(width, height, minDims.minLongSide, minDims.minShortSide)) {
        addToast({
          message: t('doka.imageTooSmall', {
            width,
            height,
            minLongSide: minDims.minLongSide,
            minShortSide: minDims.minShortSide,
          }),
          variant: 'danger',
        })
        return false
      }
    }
    catch {
      addToast({ message: t('doka.imageReadError'), variant: 'danger' })
      return false
    }

    return true
  }

  /**
   * Listing crop exports must stay at least `minShortSide` on both edges so free-aspect
   * crops cannot shrink to a tiny slice.
   *
   * @param blob The processed crop blob
   * @param mimeType The mime type of the blob
   * @returns True when the crop meets the listing short-side floor
   */
  async function validateProcessedListingCropMeetsMinimum(blob: Blob, mimeType: string): Promise<boolean> {
    const minDims = options?.minimumImageDimensions
    if (!minDims || !(mimeType ?? '').startsWith('image/') || !(blob instanceof Blob)) {
      return true
    }

    try {
      const { width, height } = await getDecodedImageDimensionsFromBlob(blob)
      if (!meetsMinimumImageDimensions(width, height, minDims.minShortSide, minDims.minShortSide)) {
        addToast({
          message: t('doka.imageCropBelowSource'),
          variant: 'danger',
        })
        return false
      }
    }
    catch {
      addToast({ message: t('doka.imageReadError'), variant: 'danger' })
      return false
    }

    return true
  }

  async function validateGrandfatheredProcessedImagePreservesSource(
    dest: Blob,
    mimeType: string,
    sourceDimensions: { width: number, height: number } | undefined,
  ): Promise<boolean> {
    if (!sourceDimensions || !(mimeType ?? '').startsWith('image/') || !(dest instanceof Blob)) {
      return true
    }

    try {
      const { width, height } = await getDecodedImageDimensionsFromBlob(dest)
      if (!processedGrandfatheredOutputPreservesSource(
        width,
        height,
        sourceDimensions.width,
        sourceDimensions.height,
      )) {
        addToast({
          message: t('doka.imageCropBelowSource'),
          variant: 'danger',
        })
        return false
      }
    }
    catch {
      addToast({ message: t('doka.imageReadError'), variant: 'danger' })
      return false
    }

    return true
  }

  const minimumImageDimensionHooks = options?.minimumImageDimensions
    ? {
        beforeOpenEditor: (file: UppyFile<Meta, Body>) =>
          validateLocalImageBlobMeetsConfiguredMinimum(file.data as Blob, file.type ?? ''),
        resolveImageCropMinSize: resolveListingPhotoCropMinSize,
        beforeAddProcessedFile: (dest: Blob & { type?: string }) =>
          validateProcessedListingCropMeetsMinimum(dest, dest.type ?? ''),
      }
    : undefined

  const handleFileWithEditor = options?.attachEditor
    ? useEditorWithUppy(
        openEditor,
        () => editorOptions.value,
        uppy,
        () => files.value.length,
        maxNumberOfFiles,
        resolveOpenCropAspectRatioForUppyFile,
        minimumImageDimensionHooks?.beforeOpenEditor,
        minimumImageDimensionHooks?.resolveImageCropMinSize,
        minimumImageDimensionHooks?.beforeAddProcessedFile,
      )
    : null

  uppy.setOptions({
    onBeforeFileAdded: (file) => {
      // Check for allowed file types.
      if (!validateDroppedFile(file)) {
        addToast({
          message: t('doka.unsupportedType', { extension: file.extension, extensions: allowedFileTypes.join(', ') }),
          variant: 'danger',
        })

        return false
      }

      if (!validateLocalImageBlobWithinMaxUploadSize(
        file.data as Blob,
        file.type ?? '',
        file.extension ?? file.name,
      )) {
        return false
      }

      if (handleFileWithEditor) {
        return handleFileWithEditor(file)
      }

      const isNewFile = !file?.meta?.replaceFileId && !file?.meta?.didEdit
      if (isNewFile && files.value.length >= maxNumberOfFiles) {
        uppy.emit('error', Error('max-files-exceeded'))
        return false
      }

      return true
    },
  })

  function onFilesAdded(addedFiles: UppyFile<Meta, Body>[]) {
    addedFiles.forEach((file) => {
      const fileToAdd = createFileInterface({
        uploaderId: file.id,
        name: file.name,
        size: file.size ?? 0,
        progress: file.progress.percentage,
        replaceFile: file.meta.replaceFileId ? files.value.find((i) => i.id === file.meta.replaceFileId) : undefined,
      })

      if (fileToAdd.replaceFile) {
        replaceFileInFiles(files.value, fileToAdd.replaceFile, fileToAdd)
      }
      else {
        files.value.push(fileToAdd)
      }
    })
  }

  async function toStableBlob(file: File): Promise<Blob> {
    const buffer = await file.arrayBuffer() // reads all bytes into memory immediately
    return new Blob([buffer], { type: file.type })
  }

  const { $captureError } = useNuxtApp()
  async function addLocalFiles(event: Event) {
    const input = event.target as Nullable<HTMLInputElement>
    const rawFiles = Array.from(input?.files ?? [])

    try {
      // Reject oversized files up front using File.size/type/name, no bytes read yet.
      const acceptedRawFiles = rawFiles.filter((rawFile) =>
        validateLocalImageBlobWithinMaxUploadSize(rawFile, rawFile.type ?? '', rawFile.name),
      )

      // Snapshot the surviving files' bytes immediately, before any per-file async
      // validation delays expose later files to purge on Android.
      const stableFiles = await Promise.all(
        acceptedRawFiles.map(async (rawFile) => ({ rawFile, stableBlob: await toStableBlob(rawFile) })),
      )

      for (const { rawFile, stableBlob } of stableFiles) {
        if (!(await validateLocalImageBlobMeetsConfiguredMinimum(stableBlob, rawFile.type ?? ''))) {
          continue
        }

        try {
          uppy.addFile({
            name: rawFile.name ?? '',
            type: rawFile.type ?? '',
            data: stableBlob,
          })
        }
        catch (err) {
          const error = err as RestrictionError<Meta, Body>
          if (!error?.isRestriction) {
            $captureError(error)
          }
        }
      }
    }
    finally {
      if (input) {
        input.value = ''
      }
    }
  }

  function findFile(fileId?: string) {
    return files.value.find((i) => i.uploaderId === fileId)
  }

  function onUploadProgress(file: UppyFile<Meta, Record<string, never>> | undefined, { bytesTotal, bytesUploaded }: FileProgressStarted) {
    const fileToUpdate = findFile(file?.id)

    if (fileToUpdate) {
      fileToUpdate.progress = (bytesUploaded * 100) / (bytesTotal || 1)
    }
  }

  function onUploadSuccess(file?: UppyFile<Meta, Body>) {
    const fileToUpdate = findFile(file?.id)

    if (fileToUpdate) {
      fileToUpdate.s3ObjectKey = file?.meta.s3ObjectKey?.toString()
    }

    if (!uppy.getFile(file?.id ?? '')?.preview) {
      uppy.removeFile(file?.id ?? '')
    }
  }

  function onComplete(result: UploadResult<Meta, Record<string, never>>) {
    result.successful?.forEach((file) => {
      const fileToUpdate = findFile(file.id)

      if (fileToUpdate) {
        fileToUpdate.s3ObjectKey = file.meta.s3ObjectKey?.toString()
      }
    })
  }

  function onThumbnailGenerated(file: UppyFile<Meta, Body>) {
    const fileToUpdate = findFile(file.id)

    if (fileToUpdate) {
      fileToUpdate.thumbnail = file.preview
    }
  }

  async function onFilesAddedMaybeRejectBelowMinimumDimensionsThenUpload(
    addedFiles: UppyFile<Meta, Body>[],
  ) {
    for (const file of addedFiles) {
      const isNewUnedited = !file?.meta?.replaceFileId && !file?.meta?.didEdit
      if (!isNewUnedited) {
        continue
      }

      const blob = file.data as Blob
      const mimeType = file.type ?? ''
      const withinMaxUploadSize = validateLocalImageBlobWithinMaxUploadSize(
        blob,
        mimeType,
        file.extension ?? file.name,
      )
      const meetsMinimumDimensions = await validateLocalImageBlobMeetsConfiguredMinimum(blob, mimeType)

      if (!withinMaxUploadSize || !meetsMinimumDimensions) {
        uppy.removeFile(file.id)
        const idx = files.value.findIndex((f) => f.uploaderId === file.id)
        if (idx !== -1) {
          files.value.splice(idx, 1)
        }
      }
    }

    void uppy.upload().catch((err: unknown) => {
      const error = err as { isRestriction?: boolean, message?: string, stack?: string }
      if (!error?.isRestriction) {
        uppy.log(error?.stack || error?.message || err)
      }
    })
  }

  uppy.on('files-added', onFilesAdded)
  if (deferAutoProceedUntilAfterLocalMinimumImageValidation) {
    uppy.on('files-added', (addedFiles) => {
      void onFilesAddedMaybeRejectBelowMinimumDimensionsThenUpload(addedFiles)
    })
  }
  uppy.on('upload-progress', onUploadProgress)
  uppy.on('upload-success', onUploadSuccess)
  uppy.on('complete', onComplete)
  uppy.on('thumbnail:generated', onThumbnailGenerated)

  async function openFileWithDoka(file: FileInterface) {
    const isPersisted = isPersistedListingPhoto(file)
    const minDims = options?.minimumImageDimensions

    const sourceDimensions = minDims && isPersisted && file.original
      ? await readImageDimensions(file.original)
      : undefined

    const sourceMeetsListingMinimum = Boolean(
      sourceDimensions
      && minDims
      && meetsMinimumImageDimensions(
        sourceDimensions.width,
        sourceDimensions.height,
        minDims.minLongSide,
        minDims.minShortSide,
      ),
    )

    // Sub-minimum or unreadable persisted photos: allow edit, floor crop at the full source frame.
    const enforceGrandfatheredCropFloorOnReEdit = Boolean(
      minDims && isPersisted && (!sourceDimensions || !sourceMeetsListingMinimum),
    )

    const enforceListingMinimumOnReEdit = Boolean(
      minDims && (!isPersisted || sourceMeetsListingMinimum),
    )

    let openOptions = {
      ...editorOptions.value,
      src: file.original,
      ...(enforceListingMinimumOnReEdit
        ? { imageCropMinSize: resolveListingPhotoCropMinSize() }
        : {}),
      ...(enforceGrandfatheredCropFloorOnReEdit && sourceDimensions
        ? {
            imageCropMinSize: resolveGrandfatheredPhotoCropMinSize(
              sourceDimensions.width,
              sourceDimensions.height,
              0,
            ),
          }
        : {}),
    }

    let bindGrandfatheredPhotoEditor: ((editor: ReturnType<typeof openEditor>) => void) | undefined

    if (enforceGrandfatheredCropFloorOnReEdit) {
      const enhanced = enhanceEditorOptionsForGrandfatheredPhotos(openOptions)
      openOptions = enhanced.editorOptions
      bindGrandfatheredPhotoEditor = enhanced.bindEditor
    }

    const editor = openEditor(openOptions)

    if (enforceGrandfatheredCropFloorOnReEdit) {
      bindGrandfatheredPhotoEditor?.(editor)
      attachPinturaGrandfatheredPhotoEditorHooks(editor)
    }

    editor.on('process', async ({ dest }) => {
      if (!dest) {
        return
      }

      if (enforceListingMinimumOnReEdit) {
        if (!(await validateProcessedListingCropMeetsMinimum(dest, dest.type ?? ''))) {
          return
        }
      }
      else if (enforceGrandfatheredCropFloorOnReEdit) {
        if (!(await validateGrandfatheredProcessedImagePreservesSource(
          dest,
          dest.type ?? '',
          sourceDimensions,
        ))) {
          return
        }
      }

      uppy.addFile({
        name: dest.name,
        type: dest.type,
        data: dest,
        meta: {
          replaceFileId: file.id,
        },
      })
    })
  }

  /**
   * Manually validates if the file is supported or not.
   * This is needed because drag & drop doesn't automatically check the allowed
   * file types on drop, and will just open the editor. Which results in nothing
   * happening for the user.
   *
   * @param file The file to validate
   * @returns True if the file is supported, false otherwise
   */
  function validateDroppedFile(file: UppyFile<Meta, Record<string, never>>) {
    const extension = file.extension?.toLowerCase()
      ?? file.name?.split('.').pop()?.toLowerCase()
      ?? ''

    return allowedFileTypes.includes(`.${extension}`)
  }

  return {
    uppy,
    allowedFileTypes,
    addLocalFiles,
    files,
    openFileWithDoka,
    remainingMaxNumberOfFiles,
  }
}
